import {
  ProverClient,
  createJoinSplitProverWorker,
  sha256Hex,
  type BrowserKeyManifest,
  type ProveRequest,
  type ProveResponse,
  type ProverProgressEvent,
} from "@stealthos/joinsplit-prover-wasm";
import { EVM_CONFIG, isProductionChainId } from "../config";
import { flatJoinSplitV2ProverRequired } from "../networks/registry";
import { preparePublicKeyCache } from "./publicKeyCache";

const MAX_MANIFEST_BYTES = 1_000_000;
const MANIFEST_FETCH_TIMEOUT_MS = 30_000;

let manifestPromise: Promise<BrowserKeyManifest> | null = null;
let activeClient: ProverClient | null = null;
let provingTail: Promise<void> = Promise.resolve();

function abortError(): Error {
  return new DOMException("JoinSplit proving was cancelled", "AbortError");
}

function manifestTimeoutError(): Error {
  return new DOMException("JoinSplit manifest fetch timed out", "TimeoutError");
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    for (const nested of Object.values(value as Record<string, unknown>)) {
      deepFreeze(nested);
    }
    Object.freeze(value);
  }
  return value;
}

function pageBaseUrl(): URL {
  const href = globalThis.location?.href;
  if (!href) {
    throw new Error("JoinSplit browser URL policy requires a page origin");
  }
  return new URL(href);
}

function configuredUrl(
  value: string,
  label: string,
  options: { sameOriginInProduction: boolean },
): URL {
  let parsed: URL;
  const page = pageBaseUrl();
  try {
    parsed = new URL(value, page);
  } catch {
    throw new Error(`${label} is not a valid URL`);
  }
  if (
    !["http:", "https:"].includes(parsed.protocol) ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    parsed.hash !== "" ||
    parsed.search !== ""
  ) {
    throw new Error(
      `${label} must be an uncredentialed HTTP(S) URL without query or fragment`,
    );
  }
  if (process.env.NODE_ENV === "production") {
    if (page.protocol !== "https:" || parsed.protocol !== "https:") {
      throw new Error(`${label} must use HTTPS in production`);
    }
    if (options.sameOriginInProduction && parsed.origin !== page.origin) {
      throw new Error(`${label} must be same-origin in production`);
    }
  }
  return parsed;
}

function assertConfiguredUrls(): void {
  const config = EVM_CONFIG.JOINSPLIT_V2;
  configuredUrl(config.BROWSER_MANIFEST_URL, "JoinSplit manifest URL", {
    sameOriginInProduction: true,
  });
  configuredUrl(config.BUNDLED_KEY_BASE_URL, "JoinSplit bundled-key URL", {
    sameOriginInProduction: true,
  });
  configuredUrl(config.KEY_CDN_BASE_URL, "JoinSplit key-CDN URL", {
    sameOriginInProduction: false,
  });
}

function assertConfigured(): void {
  const config = EVM_CONFIG.JOINSPLIT_V2;
  // The artifacts are network independent: the flat V2 EVM stack and every
  // Solana network prove with the same keys.
  if (!flatJoinSplitV2ProverRequired()) {
    throw new Error("Flat JoinSplit V2 is not enabled in this build");
  }
  if (
    !config.BROWSER_MANIFEST_URL ||
    !config.BROWSER_MANIFEST_SHA256 ||
    !config.BUNDLED_KEY_BASE_URL ||
    !config.KEY_CDN_BASE_URL
  ) {
    throw new Error(
      "Flat JoinSplit V2 browser manifest and key locations are not configured",
    );
  }
  if (
    isProductionChainId(EVM_CONFIG.CHAIN_ID) &&
    config.ALLOW_DEVELOPMENT_ARTIFACTS
  ) {
    throw new Error(
      "Development proving artifacts are forbidden on production chains",
    );
  }
  assertConfiguredUrls();
}

async function readBoundedManifest(
  response: Response,
  signal: AbortSignal,
): Promise<Uint8Array> {
  if (!response.body) {
    throw new Error("JoinSplit manifest response is not a readable stream");
  }
  const output = new Uint8Array(MAX_MANIFEST_BYTES);
  const reader = response.body.getReader();
  let offset = 0;
  try {
    while (true) {
      if (signal.aborted) throw signal.reason ?? manifestTimeoutError();
      const { done, value } = await new Promise<
        ReadableStreamReadResult<Uint8Array>
      >((resolve, reject) => {
        const onAbort = () => {
          signal.removeEventListener("abort", onAbort);
          reject(signal.reason ?? manifestTimeoutError());
        };
        signal.addEventListener("abort", onAbort, { once: true });
        reader.read().then(
          (result) => {
            signal.removeEventListener("abort", onAbort);
            resolve(result);
          },
          (error) => {
            signal.removeEventListener("abort", onAbort);
            reject(error);
          },
        );
      });
      if (done) break;
      if (!value || value.byteLength === 0) continue;
      if (offset + value.byteLength > MAX_MANIFEST_BYTES) {
        await reader.cancel("manifest exceeds maximum byte length");
        throw new Error("JoinSplit manifest exceeds the maximum byte length");
      }
      output.set(value, offset);
      offset += value.byteLength;
    }
  } catch (error) {
    if (signal.aborted) {
      try {
        await reader.cancel(signal.reason);
      } catch {
        // The fetch implementation may already have errored the stream.
      }
      throw signal.reason ?? manifestTimeoutError();
    }
    throw error;
  } finally {
    reader.releaseLock();
  }
  if (offset === 0) {
    throw new Error("JoinSplit manifest has an invalid byte length");
  }
  return output.slice(0, offset);
}

async function loadPinnedManifest(): Promise<BrowserKeyManifest> {
  assertConfigured();
  const config = EVM_CONFIG.JOINSPLIT_V2;
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(
    () => controller.abort(manifestTimeoutError()),
    MANIFEST_FETCH_TIMEOUT_MS,
  );
  let bytes: Uint8Array;
  try {
    const response = await fetch(config.BROWSER_MANIFEST_URL, {
      method: "GET",
      credentials: "omit",
      redirect: "error",
      cache: "force-cache",
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`JoinSplit manifest fetch failed (${response.status})`);
    }
    const declaredLength = response.headers.get("content-length");
    let expectedLength: number | null = null;
    if (declaredLength !== null) {
      const parsedLength = Number(declaredLength);
      if (
        !Number.isSafeInteger(parsedLength) ||
        parsedLength <= 0 ||
        parsedLength > MAX_MANIFEST_BYTES
      ) {
        throw new Error("JoinSplit manifest Content-Length is invalid");
      }
      expectedLength = parsedLength;
    }
    bytes = await readBoundedManifest(response, controller.signal);
    if (expectedLength !== null && bytes.byteLength !== expectedLength) {
      throw new Error(
        "JoinSplit manifest Content-Length does not match its body",
      );
    }
  } catch (error) {
    if (controller.signal.aborted) {
      throw controller.signal.reason ?? manifestTimeoutError();
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeout);
  }
  const actualHash = await sha256Hex(bytes);
  if (actualHash !== config.BROWSER_MANIFEST_SHA256.toLowerCase()) {
    throw new Error(
      "JoinSplit manifest does not match the build-time SHA-256 pin",
    );
  }
  let manifest: BrowserKeyManifest;
  try {
    manifest = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new Error("JoinSplit manifest is not valid JSON");
  }
  if (
    !manifest ||
    typeof manifest !== "object" ||
    !Array.isArray(manifest.keys) ||
    manifest.keys.length !== 5
  ) {
    throw new Error("JoinSplit manifest has an invalid release shape");
  }
  if (manifest.is_development_setup && !config.ALLOW_DEVELOPMENT_ARTIFACTS) {
    throw new Error("JoinSplit manifest contains development proving keys");
  }
  // The current exporter records lock provenance by path + digest, whereas
  // the packaged worker also expects the older explicit `present` flag.
  // Derive that flag only from a complete SHA-pinned schema-3 lock record,
  // after verifying the original manifest bytes above. No key pin, source
  // record, setup marker, or served artifact is changed.
  const lock = manifest.artifact_lock as unknown as Record<string, unknown>;
  if (lock && typeof lock === "object" && lock.present === undefined) {
    if (
      lock.schema_version !== 3 ||
      lock.path !== "base-decider/artifacts/joinsplit/joinsplit-lock.json" ||
      typeof lock.sha256 !== "string" ||
      !/^[a-f0-9]{64}$/.test(lock.sha256) ||
      typeof lock.source_git_commit !== "string" ||
      !/^[a-f0-9]{40}$/.test(lock.source_git_commit) ||
      typeof lock.warning !== "string"
    )
      throw new Error("JoinSplit manifest lock provenance is incomplete");
    manifest = {
      ...manifest,
      artifact_lock: { ...manifest.artifact_lock, present: true },
    };
  }
  // JSON.parse already created a detached object graph. Freeze the complete
  // graph so neither a client nor a later proof can mutate the pinned policy.
  return deepFreeze(manifest);
}

async function pinnedManifest(): Promise<BrowserKeyManifest> {
  if (!manifestPromise) {
    manifestPromise = loadPinnedManifest().catch((error) => {
      manifestPromise = null;
      throw error;
    });
  }
  return manifestPromise;
}

function awaitWithAbort<T>(
  promise: Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener("abort", onAbort);
      reject(abortError());
    };
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      },
    );
  });
}

async function waitForTurn(signal?: AbortSignal): Promise<() => void> {
  if (signal?.aborted) throw abortError();
  const previous = provingTail;
  let releaseGate!: () => void;
  let released = false;
  const gate = new Promise<void>((resolve) => {
    releaseGate = resolve;
  });
  // Keep the queue chained to the preceding proof even if this waiter is
  // cancelled before it reaches the front. A later proof must never overtake
  // the still-active predecessor.
  provingTail = previous.then(() => gate);
  const release = () => {
    if (released) return;
    released = true;
    releaseGate();
  };
  try {
    await awaitWithAbort(previous, signal);
  } catch (error) {
    release();
    throw error;
  }
  return release;
}

/**
 * Runs one proof in an isolated first-party module worker. The worker is
 * always terminated after the callback, which is the hard witness-erasure
 * boundary for the WASM heap. Proofs are serialized because cancellation
 * terminates the complete worker instance.
 */
export async function withFlatJoinSplitV2Prover<T>(
  callback: (prover: {
    prove(request: ProveRequest, signal?: AbortSignal): Promise<ProveResponse>;
  }) => Promise<T>,
  options: {
    signal?: AbortSignal;
    onProgress?: (event: ProverProgressEvent) => void;
  } = {},
): Promise<T> {
  // Public manifest I/O and validation happen outside the witness/proving
  // mutex. A slow metadata origin must not extend an active worker's lifetime.
  const manifest = await awaitWithAbort(pinnedManifest(), options.signal);
  const release = await waitForTurn(options.signal);
  let client: ProverClient | null = null;
  try {
    if (options.signal?.aborted) throw abortError();
    const config = EVM_CONFIG.JOINSPLIT_V2;
    let phase: ProverProgressEvent["phase"] | "initializing" = "initializing";
    const createClient = () =>
      new ProverClient({
        manifest,
        createWorker: createJoinSplitProverWorker,
        keySource: {
          bundledBaseUrl: configuredUrl(
            config.BUNDLED_KEY_BASE_URL,
            "JoinSplit bundled-key URL",
            { sameOriginInProduction: true },
          ).toString(),
          cdnBaseUrl: configuredUrl(
            config.KEY_CDN_BASE_URL,
            "JoinSplit key-CDN URL",
            { sameOriginInProduction: false },
          ).toString(),
        },
        allowDevelopmentSetup: config.ALLOW_DEVELOPMENT_ARTIFACTS,
        proveTimeoutMs: config.PROVE_TIMEOUT_MS,
        keyLoadTimeoutMs: config.KEY_LOAD_TIMEOUT_MS,
        onProgress: (event) => {
          phase = event.phase;
          options.onProgress?.(event);
        },
      });
    client = createClient();
    activeClient = client;
    return await callback({
      prove: async (request, signal) => {
        // Snapshot before public-data I/O, so the caller cannot change the
        // request while a key download or a worker restart is pending.
        const snapshot = structuredClone(request);
        if (process.env.REACT_APP_PRIVATE_FEE_POOL) {
          options.onProgress?.({
            phase: "fetching-key",
            arityId: snapshot.arity_id,
          });
          await preparePublicKeyCache(
            manifest,
            snapshot.arity_id,
            config.KEY_CDN_BASE_URL,
            signal || options.signal,
          );
        }
        const proofSignal = signal || options.signal;
        // Retry only a trapped worker, before any proof has left this method.
        // Never retry a rejected witness, invalid proof, timeout or cancellation.
        for (let attempt = 0; ; attempt++) {
          try {
            return await client!.prove(snapshot, proofSignal);
          } catch (error) {
            if (proofSignal?.aborted) throw abortError();
            const trapped =
              error instanceof Error &&
              /^(?:unreachable|memory access out of bounds|out of memory|WebAssembly\.Memory\.grow\(\): Maximum memory size exceeded)$/i.test(
                error.message,
              );
            if (!trapped) throw error;
            // A trapped WASM instance must never be reused, including its heap.
            client!.dispose();
            client = null;
            activeClient = null;
            if (attempt > 0) {
              throw new Error(
                `Browser proof stopped at ${phase} (WASM_TRAP, circuit ${snapshot.arity_id}). No transaction was sent.`,
              );
            }
            options.onProgress?.({
              phase: "resolving-key",
              arityId: snapshot.arity_id,
              detail: "fresh-worker-retry",
            });
            phase = "initializing";
            client = createClient();
            activeClient = client;
          }
        }
      },
    });
  } finally {
    client?.dispose();
    if (activeClient === client) activeClient = null;
    release();
  }
}

/** Abort/dispose the active prover on disconnect, account change, or unmount. */
export function disposeFlatJoinSplitV2Prover(): void {
  activeClient?.dispose();
  activeClient = null;
}

/** Test-only reset for pinned public metadata; it never contains witnesses. */
export function resetFlatJoinSplitV2ManifestCacheForTests(): void {
  manifestPromise = null;
}
